import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDate } from '../lib/dateUtils';

export const FundsFlowView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);

  const fundsFlowData = useLiveQuery(async () => {
    if (!activeCompany) return null;

    const sources: any[] = [];
    const uses: any[] = [];

    // 1. Profit (Source if positive, Use if negative)
    const incomeGroups = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.nature === 'Income').toArray();
    const expenseGroups = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.nature === 'Expenses').toArray();
    
    let totalIncome = 0;
    let totalExpense = 0;

    const getGroupBalance = async (groupId: number, start: string, end: string, visitedGroupIds = new Set<number>()) => {
      let bal = 0;
      
      if (visitedGroupIds.has(groupId)) return 0;
      visitedGroupIds.add(groupId);

      let includeOpening = false;
      if (activeCompany.financialYearStart && start <= activeCompany.financialYearStart) {
        includeOpening = true;
      }

      const ledgers = await db.ledgers.where('groupId').equals(groupId).toArray();
      for (const l of ledgers) {
        if (includeOpening) {
           bal += (l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance);
        }
        const entries = await db.voucherEntries.where('ledgerId').equals(l.id!).toArray();
        for (const e of entries) {
          const v = await db.vouchers.get(e.voucherId);
          if (v && !v.deleted && v.date >= start && v.date <= end) {
            bal += (e.type === 'Dr' ? e.amount : -e.amount);
          }
        }
      }
      const subGroups = await db.groups.where('parentGroupId').equals(groupId).toArray();
      for (const sg of subGroups) {
        bal += await getGroupBalance(sg.id!, start, end, visitedGroupIds);
      }
      return bal;
    };

    for (const g of incomeGroups) totalIncome += Math.abs(await getGroupBalance(g.id!, fromDate, toDate));
    for (const g of expenseGroups) totalExpense += Math.abs(await getGroupBalance(g.id!, fromDate, toDate));

    const profit = totalIncome - totalExpense;
    if (profit > 0) sources.push({ name: 'Net Profit from Operations', amount: profit });
    else if (profit < 0) uses.push({ name: 'Net Loss from Operations', amount: Math.abs(profit) });

    // 2. Schedule of Changes in Working Capital
    const isCurrentGroup = async (groupId: number, type: 'Assets' | 'Liabilities', visited = new Set<number>()): Promise<boolean> => {
      if (visited.has(groupId)) return false;
      visited.add(groupId);
      const g = await db.groups.get(groupId);
      if (!g) return false;
      if (g.name === `Current ${type}`) return true;
      if (g.parentGroupId) return await isCurrentGroup(g.parentGroupId, type, visited);
      return false;
    };

    let totalCAStart = 0, totalCAEnd = 0;
    let totalCLStart = 0, totalCLEnd = 0;

    // We need to keep a top-level tracker so we dont double count CA/CL components by iterating allGroups blindly
    const processedGroups = new Set<number>();

    // 3. Changes in Assets and Liabilities (Non-Current)
    const allGroups = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
    for (const group of allGroups) {
      if (group.nature === 'Income' || group.nature === 'Expenses') continue;
      // If we already rolled this up into a parent, skip it
      if (processedGroups.has(group.id!)) continue;
      
      const isCA = await isCurrentGroup(group.id!, 'Assets');
      const isCL = await isCurrentGroup(group.id!, 'Liabilities');
      
      if (isCA || isCL) {
         // It's part of working capital. Let it be processed ONLY by the top-level trigger.
         if (group.name === 'Current Assets') {
           totalCAStart = await getGroupBalance(group.id!, '1900-01-01', fromDate);
           totalCAEnd = await getGroupBalance(group.id!, '1900-01-01', toDate);
         } else if (group.name === 'Current Liabilities') {
           totalCLStart = Math.abs(await getGroupBalance(group.id!, '1900-01-01', fromDate));
           totalCLEnd = Math.abs(await getGroupBalance(group.id!, '1900-01-01', toDate));
         }
         continue; // Skips adding sources/uses directly
      }

      // If it has a parent, skip it here and let it be walked by its parent to avoid double-processing
      if (group.parentGroupId) continue;

      const balStart = await getGroupBalance(group.id!, '1900-01-01', fromDate);
      const balEnd = await getGroupBalance(group.id!, '1900-01-01', toDate);
      const change = balEnd - balStart;

      if (Math.abs(change) < 0.01) continue;

      if (group.nature === 'Liabilities') {
        if (change < 0) sources.push({ name: `Increase in ${group.name}`, amount: Math.abs(change) });
        else uses.push({ name: `Decrease in ${group.name}`, amount: change });
      } else if (group.nature === 'Assets') {
        if (change > 0) uses.push({ name: `Increase in ${group.name}`, amount: change });
        else sources.push({ name: `Decrease in ${group.name}`, amount: Math.abs(change) });
      }
    }

    const wcStart = totalCAStart - totalCLStart;
    const wcEnd = totalCAEnd - totalCLEnd;
    const wcChange = wcEnd - wcStart;

    if (wcChange > 0) {
       uses.push({ name: 'Net Increase in Working Capital', amount: wcChange });
    } else if (wcChange < 0) {
       sources.push({ name: 'Net Decrease in Working Capital', amount: Math.abs(wcChange) });
    }

    const totalSources = sources.reduce((sum, s) => sum + s.amount, 0);
    const totalUses = uses.reduce((sum, u) => sum + u.amount, 0);

    return { sources, uses, totalSources, totalUses };
  }, [activeCompany, fromDate, toDate]);

  if (!fundsFlowData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Calculating Funds Flow...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Funds Flow</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span className="text-[#1e8585]">Statement of Sources and Application of Funds</span>
        <span>{formatDate(fromDate)} to {formatDate(toDate)}</span>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col border-r border-[#aebec8]">
          <div className="bg-gray-100 px-2 py-1 font-bold border-b border-[#aebec8] text-[#1e8585]">Sources of Funds</div>
          <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
            {fundsFlowData?.sources.map((s, i) => (
              <div key={i} className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-700 italic">{s.name}</span>
                <span className="font-bold">₹{s.amount.toFixed(2)}</span>
              </div>
            ))}
            {fundsFlowData?.sources.length === 0 && <div className="text-center text-gray-400 mt-4 italic">No sources identified</div>}
          </div>
          <div className="bg-[#f0fdfa] px-2 py-2 font-bold border-t border-[#aebec8] flex justify-between text-[14px]">
            <span>Total Sources</span>
            <span>₹{fundsFlowData?.totalSources.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="bg-gray-100 px-2 py-1 font-bold border-b border-[#aebec8] text-red-700">Application of Funds</div>
          <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
            {fundsFlowData?.uses.map((u, i) => (
              <div key={i} className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-700 italic">{u.name}</span>
                <span className="font-bold">₹{u.amount.toFixed(2)}</span>
              </div>
            ))}
            {fundsFlowData?.uses.length === 0 && <div className="text-center text-gray-400 mt-4 italic">No applications identified</div>}
          </div>
          <div className="bg-[#fef2f2] px-2 py-2 font-bold border-t border-[#aebec8] flex justify-between text-[14px]">
            <span>Total Applications</span>
            <span>₹{fundsFlowData?.totalUses.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto">
        <div className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100" onClick={() => handleItemSelect({ name: 'Quit' })}>
          <div className="flex items-center space-x-[2px] whitespace-nowrap">
            <span className="text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2">Q</span>
            <span className="text-[#2a62a3] font-bold">:</span><span className="text-black leading-none">Quit</span>
          </div>
        </div>
      </div>
    </div>
  );
};
